import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Save, 
  ArrowLeft,
  MapPin,
  Building2,
  Truck,
  Package,
  Users,
  Factory,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { 
  getAllSpecies, 
  getSettings, 
  getAllDestinatari, 
  getAllDestinatariDDT,
  getAllProduttori, 
  getAllUbicazioni,
  getNextDDRNumber,
  createDDR,
  initializeDB,
  getLastDDR,
  getAllCausali
} from "@/services/localDatabase";
import { downloadDDRPdf, downloadDDTPdf, downloadBothPdf } from "@/services/pdfGenerator";

const NewDDR = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingType, setLoadingType] = useState(null); // 'ddr', 'ddt', 'both'
  const [species, setSpecies] = useState([]);
  const [settings, setSettings] = useState(null);
  const [destinatari, setDestinatari] = useState([]);
  const [destinatariDDT, setDestinatariDDT] = useState([]);
  const [produttori, setProduttori] = useState([]);
  const [ubicazioni, setUbicazioni] = useState([]);
  const [causali, setCausali] = useState([]);
  const [selectedDestinatario, setSelectedDestinatario] = useState("");
  const [selectedDestinatarioDDT, setSelectedDestinatarioDDT] = useState("");
  const [selectedProduttore, setSelectedProduttore] = useState("settings");
  const [nextNumber, setNextNumber] = useState(1);
  const [numeroDDR, setNumeroDDR] = useState(1);
  const [serieDDR, setSerieDDR] = useState("AP");
  const [specieManuale, setSpecieManuale] = useState(false);
  const [tipoVettore, setTipoVettore] = useState("manuale"); // 'mittente', 'destinatario', 'manuale'
  const [lastDDR, setLastDDR] = useState(null);
  const [destinazioneDDTDiversa, setDestinazioneDDTDiversa] = useState(false);
  
  const [formData, setFormData] = useState({
    produttore: null,
    zona_produzione: "ALLEVAMENTO",
    data_raccolta: "",
    stato_sanitario: "B",
    ubicazione_zona: "",
    provenienza: {
      tipo: "",
      ubicazione: "",
      indirizzo: "",
      durata: "",
      data_ingresso: "",
      data_uscita: ""
    },
    specie: "",
    n_colli: 0,
    peso_kg: 0,
    lotto: "",
    destinazione: {
      tipo: "depurazione",
      numero_riconoscimento: "",
      nome: "",
      via: "",
      comune: "",
      provincia: ""
    },
    identificazione_vettore: "",
    causale_text: "",
    data_documento: format(new Date(), "dd/MM/yyyy")
  });

  const [dataRaccolta, setDataRaccolta] = useState(new Date());
  const [dataDocumento, setDataDocumento] = useState(new Date());
  const [dataIngresso, setDataIngresso] = useState(null);
  const [dataUscita, setDataUscita] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      await initializeDB();
      const [speciesData, settingsData, destinatariData, destinatariDDTData, produttoriData, ubicazioniData, causaliData, nextNumData, lastDDRData] = await Promise.all([
        getAllSpecies(),
        getSettings(),
        getAllDestinatari(),
        getAllDestinatariDDT(),
        getAllProduttori(),
        getAllUbicazioni(),
        getAllCausali(),
        getNextDDRNumber(),
        getLastDDR()
      ]);
      
      setSpecies(speciesData);
      setSettings(settingsData);
      setDestinatari(destinatariData);
      setDestinatariDDT(destinatariDDTData);
      setProduttori(produttoriData);
      setUbicazioni(ubicazioniData);
      setCausali(causaliData);
      setLastDDR(lastDDRData);
      
      // Set next DDR number
      setNextNumber(nextNumData.next_number);
      setNumeroDDR(nextNumData.next_number);
      setSerieDDR(nextNumData.serie);
      
      // Preselect predefined causale
      const predefCausale = (causaliData || []).find(c => c.predefinito);
      if (predefCausale) {
        setFormData(prev => ({ ...prev, causale_text: predefCausale.testo }));
      }
      
      // Initialize produttore from settings
      if (settingsData) {
        setFormData(prev => ({
          ...prev,
          produttore: {
            tipo: "produttore",
            nome: settingsData.nome || "",
            sede_legale: settingsData.sede_legale || "",
            cap: settingsData.cap || "",
            citta: settingsData.citta || "",
            provincia: settingsData.provincia || "",
            ric: settingsData.ric || "",
            email: settingsData.email || ""
          }
        }));
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Errore nel caricamento dei dati");
    }
  };

  // Richiama dati dall'ultimo DDR
  const loadLastDDR = () => {
    if (!lastDDR) {
      toast.error("Nessun DDR precedente trovato");
      return;
    }
    
    // Load data from last DDR (excluding date, number, quantity, colli)
    setFormData(prev => ({
      ...prev,
      produttore: lastDDR.produttore || prev.produttore,
      zona_produzione: lastDDR.zona_produzione || prev.zona_produzione,
      stato_sanitario: lastDDR.stato_sanitario || prev.stato_sanitario,
      ubicazione_zona: lastDDR.ubicazione_zona || prev.ubicazione_zona,
      provenienza: lastDDR.provenienza || prev.provenienza,
      specie: lastDDR.specie || prev.specie,
      lotto: lastDDR.lotto || prev.lotto,
      destinazione: lastDDR.destinazione || prev.destinazione,
      identificazione_vettore: lastDDR.identificazione_vettore || prev.identificazione_vettore,
      causale_text: lastDDR.causale_text || prev.causale_text
    }));
    
    // Update selected destinatario if matches
    if (lastDDR.destinazione?.nome) {
      const matchingDest = destinatari.find(d => d.nome === lastDDR.destinazione.nome);
      if (matchingDest) {
        setSelectedDestinatario(matchingDest.id);
      }
    }
    
    // Update selected produttore if matches
    if (lastDDR.produttore?.nome) {
      const matchingProd = produttori.find(p => p.nome === lastDDR.produttore.nome);
      if (matchingProd) {
        setSelectedProduttore(matchingProd.id);
      }
    }
    
    toast.success("Dati ultimo DDR caricati! Modifica ciò che serve.");
  };

  const handleSelectProduttore = (prodId) => {
    setSelectedProduttore(prodId);
    if (prodId === "settings") {
      setFormData(prev => ({
        ...prev,
        produttore: {
          tipo: prev.produttore?.tipo || "produttore",
          nome: settings?.nome || "",
          sede_legale: settings?.sede_legale || "",
          cap: settings?.cap || "",
          citta: settings?.citta || "",
          provincia: settings?.provincia || "",
          ric: settings?.ric || "",
          email: settings?.email || ""
        }
      }));
    } else {
      const prod = produttori.find(p => p.id === prodId);
      if (prod) {
        setFormData(prev => ({
          ...prev,
          produttore: {
            tipo: prev.produttore?.tipo || "produttore",
            nome: prod.nome || "",
            sede_legale: prod.sede_legale || "",
            cap: prod.cap || "",
            citta: prod.citta || "",
            provincia: prod.provincia || "",
            ric: prod.ric || "",
            email: prod.email || ""
          }
        }));
      }
    }
  };

  const handleTipoChange = async (tipo) => {
    // Get new number for the selected type
    try {
      const nextNumData = await getNextDDRNumber(tipo);
      setNextNumber(nextNumData.next_number);
      setNumeroDDR(nextNumData.next_number);
      setSerieDDR(nextNumData.serie);
    } catch (e) {
      console.error("Error getting next number:", e);
    }
    
    // Load data from settings - always produttore
    setFormData(prev => ({
      ...prev,
      produttore: {
        tipo: "produttore",
        nome: settings?.nome || "",
        sede_legale: settings?.sede_legale || "",
        cap: settings?.cap || "",
        citta: settings?.citta || "",
        provincia: settings?.provincia || "",
        ric: settings?.ric || "",
        email: settings?.email || ""
      },
      provenienza: {
        tipo: "",
        ubicazione: "",
        indirizzo: "",
        durata: "",
        data_ingresso: "",
        data_uscita: ""
      }
    }));
    setSelectedProduttore("settings");
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTipoVettoreChange = (tipo) => {
    setTipoVettore(tipo);
    if (tipo === "mittente") {
      // Salva "MITTENTE" per il DDT
      setFormData(prev => ({ ...prev, identificazione_vettore: "MITTENTE" }));
    } else if (tipo === "destinatario") {
      // Salva "DESTINATARIO" per il DDT
      setFormData(prev => ({ ...prev, identificazione_vettore: "DESTINATARIO" }));
    } else {
      // Manuale - svuota per inserimento manuale
      setFormData(prev => ({ ...prev, identificazione_vettore: "" }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleSelectDestinatario = (destId) => {
    setSelectedDestinatario(destId);
    if (destId === "manual") {
      setFormData(prev => ({
        ...prev,
        destinazione: {
          tipo: "depurazione",
          numero_riconoscimento: "",
          nome: "",
          via: "",
          comune: "",
          provincia: ""
        }
      }));
    } else {
      const dest = destinatari.find(d => d.id === destId);
      if (dest) {
        setFormData(prev => ({
          ...prev,
          destinazione: {
            tipo: dest.tipo || "depurazione",
            numero_riconoscimento: dest.numero_riconoscimento || "",
            nome: dest.nome || "",
            via: dest.via || "",
            comune: dest.comune || "",
            provincia: dest.provincia || ""
          }
        }));
      }
    }
  };

  const handleSubmit = async (e, docType = 'both', vuoto = false) => {
    e.preventDefault();
    setLoading(true);
    setLoadingType(`${docType}${vuoto ? '-vuoto' : ''}`);

    try {
      // Prepare destinazione_ddt if different
      let destinazioneDDT = null;
      if (destinazioneDDTDiversa && selectedDestinatarioDDT) {
        const destDDT = destinatariDDT.find(d => d.id === selectedDestinatarioDDT);
        if (destDDT) {
          destinazioneDDT = {
            nome: destDDT.nome || "",
            via: destDDT.via || "",
            comune: destDDT.comune || "",
            provincia: destDDT.provincia || "",
            partita_iva: destDDT.partita_iva || ""
          };
        }
      }

      const submitData = {
        ...formData,
        numero_ddr: numeroDDR,
        provenienza: formData.provenienza,
        data_raccolta: format(dataRaccolta, "dd/MM/yyyy"),
        data_documento: format(dataDocumento, "dd/MM/yyyy"),
        destinazione_ddt_diversa: destinazioneDDTDiversa,
        destinazione_ddt: destinazioneDDT
      };

      const newDDR = await createDDR(submitData);
      toast.success(`DDR ${newDDR.serie}-${newDDR.numero_ddr} creato con successo!`);
      
      // Generate and download PDF based on type
      try {
        if (docType === 'ddr') {
          await downloadDDRPdf(newDDR, { vuoto });
          toast.success(vuoto ? "DDR (vuoto) scaricato" : "DDR scaricato");
        } else if (docType === 'ddt') {
          await downloadDDTPdf(newDDR, { vuoto });
          toast.success(vuoto ? "DDT (vuoto) scaricato" : "DDT scaricato");
        } else {
          await downloadBothPdf(newDDR, { vuoto });
          toast.success(vuoto ? "DDR + DDT (vuoti) scaricati" : "DDR + DDT scaricati");
        }
      } catch (pdfError) {
        console.error("PDF generation error:", pdfError);
        toast.error("DDR creato, ma errore nella generazione PDF");
      }

      navigate("/");
    } catch (error) {
      console.error("Error creating DDR:", error);
      toast.error("Errore nella creazione del DDR");
    } finally {
      setLoading(false);
      setLoadingType(null);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="new-ddr-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Nuovo DDR
          </h1>
          <p className="text-slate-500 mt-1">Documento di Registrazione Molluschi Bivalvi</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="max-w-3xl space-y-6">
          {/* Richiama ultimo DDR */}
          {lastDDR && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={loadLastDDR}
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                data-testid="load-last-ddr"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Richiama ultimo DDR ({lastDDR.serie}-{lastDDR.numero_ddr})
              </Button>
            </div>
          )}

          {/* Numero DDR */}
          <Card className="border-slate-100 border-green-200 bg-green-50/30">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Package className="w-5 h-5 text-green-600" />
                Numero Documento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Serie</Label>
                  <Input value={serieDDR} disabled className="mt-1 bg-slate-100" data-testid="serie-ddr" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Numero DDR</Label>
                  <Input
                    type="number"
                    value={numeroDDR}
                    onChange={(e) => setNumeroDDR(parseInt(e.target.value) || 1)}
                    min="1"
                    className="mt-1"
                    data-testid="numero-ddr"
                  />
                  <p className="text-xs text-slate-500 mt-1">Suggerito: {nextNumber}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Produttore Selection */}
          <Card className="border-slate-100 border-orange-200 bg-orange-50/30">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Factory className="w-5 h-5 text-orange-500" />
                Produttore
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1 block">Seleziona dall'archivio</Label>
                <Select value={selectedProduttore} onValueChange={handleSelectProduttore}>
                  <SelectTrigger data-testid="select-produttore">
                    <SelectValue placeholder="Seleziona produttore" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="settings">-- Usa impostazioni predefinite --</SelectItem>
                    {produttori.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome} - {p.citta} ({p.provincia})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.produttore && formData.produttore.nome && (
                <div className="p-3 bg-white rounded border border-slate-200 text-sm">
                  <p className="font-medium">{formData.produttore.nome}</p>
                  <p className="text-slate-500">{formData.produttore.sede_legale}, {formData.produttore.cap} {formData.produttore.citta} ({formData.produttore.provincia})</p>
                  <p className="text-slate-500">
                    RIC: {formData.produttore.ric}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Zona di Produzione */}
          <Card className="border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <MapPin className="w-5 h-5 text-orange-500" />
                Zona di Produzione
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Origine</Label>
                <RadioGroup
                  value={formData.zona_produzione}
                  onValueChange={(value) => handleInputChange("zona_produzione", value)}
                  className="flex gap-6 mt-2"
                  data-testid="zona-produzione"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ALLEVAMENTO" id="allevamento" />
                    <Label htmlFor="allevamento" className="font-normal">Allevamento</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="LIBERA_RACCOLTA" id="libera" />
                    <Label htmlFor="libera" className="font-normal">Libera Raccolta</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Data di Raccolta</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal mt-1" data-testid="data-raccolta-btn">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(dataRaccolta, "dd/MM/yyyy", { locale: it })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={dataRaccolta} onSelect={(date) => date && setDataRaccolta(date)} locale={it} />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Stato Sanitario</Label>
                  <Select value={formData.stato_sanitario} onValueChange={(value) => handleInputChange("stato_sanitario", value)}>
                    <SelectTrigger className="mt-1" data-testid="stato-sanitario">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A - Classe A</SelectItem>
                      <SelectItem value="B">B - Classe B</SelectItem>
                      <SelectItem value="C">C - Classe C</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Ubicazione Zona di Produzione</Label>
                <Select value={formData.ubicazione_zona} onValueChange={(value) => handleInputChange("ubicazione_zona", value)}>
                  <SelectTrigger className="mt-1" data-testid="ubicazione-zona">
                    <SelectValue placeholder="Seleziona ubicazione" />
                  </SelectTrigger>
                  <SelectContent>
                    {ubicazioni.map((u) => (
                      <SelectItem key={u.id} value={u.codice || u.nome_zona}>
                        {u.codice || u.nome_zona} {u.descrizione ? `- ${u.descrizione}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={formData.ubicazione_zona}
                  onChange={(e) => handleInputChange("ubicazione_zona", e.target.value)}
                  placeholder="Oppure digita manualmente"
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>

          {/* Specie e Quantità */}
          <Card className="border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Package className="w-5 h-5 text-orange-500" />
                Specie e Quantità
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-slate-700">Specie</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSpecieManuale(!specieManuale);
                      if (!specieManuale) handleInputChange("specie", "");
                    }}
                    className="text-xs text-orange-600 hover:text-orange-700"
                    data-testid="toggle-specie-manuale"
                  >
                    {specieManuale ? "Seleziona da archivio" : "Inserisci manualmente"}
                  </Button>
                </div>
                
                {specieManuale ? (
                  <Input
                    value={formData.specie}
                    onChange={(e) => handleInputChange("specie", e.target.value.toUpperCase())}
                    placeholder="es. VONGOLA VERACE (Ruditapes philippinarum)"
                    className="mt-1"
                    data-testid="specie-input-manuale"
                  />
                ) : (
                  <Select value={formData.specie} onValueChange={(value) => handleInputChange("specie", value)}>
                    <SelectTrigger className="mt-1" data-testid="specie-select">
                      <SelectValue placeholder="Seleziona specie" />
                    </SelectTrigger>
                    <SelectContent>
                      {species.map((s) => (
                        <SelectItem key={s.id} value={s.nome_scientifico ? `${s.nome_comune} (${s.nome_scientifico})` : s.nome_comune}>
                          {s.nome_comune} {s.nome_scientifico ? `(${s.nome_scientifico})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">N. Colli</Label>
                  <Input
                    type="number"
                    value={formData.n_colli}
                    onChange={(e) => handleInputChange("n_colli", parseInt(e.target.value) || 0)}
                    className="mt-1"
                    data-testid="n-colli"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Peso (Kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.peso_kg}
                    onChange={(e) => handleInputChange("peso_kg", parseFloat(e.target.value) || 0)}
                    className="mt-1"
                    data-testid="peso-kg"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Lotto</Label>
                  <Input
                    value={formData.lotto}
                    onChange={(e) => handleInputChange("lotto", e.target.value)}
                    placeholder="es. PAD"
                    className="mt-1"
                    data-testid="lotto"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Destinazione */}
          <Card className="border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Building2 className="w-5 h-5 text-orange-500" />
                Destinazione
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-orange-500" />
                  <Label className="text-sm font-medium text-slate-700">Seleziona dall'archivio</Label>
                </div>
                <Select value={selectedDestinatario} onValueChange={handleSelectDestinatario}>
                  <SelectTrigger data-testid="select-destinatario">
                    <SelectValue placeholder="Scegli destinatario o inserisci manualmente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">-- Inserimento manuale --</SelectItem>
                    {destinatari.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.nome} - {d.comune} ({d.provincia})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Tipo Destinazione</Label>
                <Select value={formData.destinazione.tipo} onValueChange={(value) => handleNestedChange("destinazione", "tipo", value)}>
                  <SelectTrigger className="mt-1" data-testid="dest-tipo">
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="depurazione">Centro di Depurazione</SelectItem>
                    <SelectItem value="spedizione">Centro di Spedizione</SelectItem>
                    <SelectItem value="trasformazione">Centro di Trasformazione</SelectItem>
                    <SelectItem value="operatore_intermedio">Operatore Intermedio</SelectItem>
                    <SelectItem value="zona_produzione">Zona di Produzione</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Numero Riconoscimento</Label>
                <Input
                  value={formData.destinazione.numero_riconoscimento}
                  onChange={(e) => handleNestedChange("destinazione", "numero_riconoscimento", e.target.value)}
                  placeholder="es. ITQ8R48"
                  className="mt-1"
                  data-testid="numero-riconoscimento"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Nome / Denominazione</Label>
                <Input
                  value={formData.destinazione.nome}
                  onChange={(e) => handleNestedChange("destinazione", "nome", e.target.value)}
                  placeholder="es. BLU MARLIN BIVALVI SRL"
                  className="mt-1"
                  data-testid="dest-nome"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Via e Numero</Label>
                <Input
                  value={formData.destinazione.via}
                  onChange={(e) => handleNestedChange("destinazione", "via", e.target.value)}
                  placeholder="es. STRADA PROVINCIALE 27"
                  className="mt-1"
                  data-testid="dest-via"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Comune</Label>
                  <Input
                    value={formData.destinazione.comune}
                    onChange={(e) => handleNestedChange("destinazione", "comune", e.target.value)}
                    placeholder="es. MESOLA"
                    className="mt-1"
                    data-testid="dest-comune"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Provincia</Label>
                  <Input
                    value={formData.destinazione.provincia}
                    onChange={(e) => handleNestedChange("destinazione", "provincia", e.target.value)}
                    placeholder="es. FE"
                    className="mt-1"
                    data-testid="dest-provincia"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Destinatario DDT Diverso */}
          <Card className="border-slate-100 border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Truck className="w-5 h-5 text-blue-500" />
                Destinatario DDT
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="dest-ddt-diverso"
                  checked={destinazioneDDTDiversa}
                  onChange={(e) => {
                    setDestinazioneDDTDiversa(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedDestinatarioDDT("");
                    }
                  }}
                  className="w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  data-testid="dest-ddt-diverso-checkbox"
                />
                <Label htmlFor="dest-ddt-diverso" className="font-medium cursor-pointer">
                  Destinatario DDT diverso dal DDR
                </Label>
              </div>

              {destinazioneDDTDiversa && (
                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-medium text-slate-700">Seleziona Destinatario DDT</Label>
                  <Select value={selectedDestinatarioDDT} onValueChange={setSelectedDestinatarioDDT}>
                    <SelectTrigger data-testid="select-destinatario-ddt">
                      <SelectValue placeholder="Scegli dall'archivio Destinatari DDT" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinatariDDT.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.nome} - {d.comune} ({d.provincia})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedDestinatarioDDT && (
                    <div className="p-3 bg-white rounded border border-blue-200 text-sm">
                      {(() => {
                        const dest = destinatariDDT.find(d => d.id === selectedDestinatarioDDT);
                        if (!dest) return null;
                        return (
                          <>
                            <p className="font-medium text-blue-700">{dest.nome}</p>
                            <p className="text-slate-500">{dest.via}, {dest.comune} ({dest.provincia})</p>
                            <p className="text-slate-500">P.IVA: {dest.partita_iva}</p>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  
                  {destinatariDDT.length === 0 && (
                    <p className="text-sm text-orange-600">
                      Nessun destinatario DDT. Aggiungine uno da: Menu → Destinatari DDT
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vettore e Data */}
          <Card className="border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Truck className="w-5 h-5 text-orange-500" />
                Vettore e Data Documento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Tipo Vettore</Label>
                <RadioGroup
                  value={tipoVettore}
                  onValueChange={handleTipoVettoreChange}
                  className="flex flex-wrap gap-4"
                  data-testid="tipo-vettore"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="mittente" id="vettore-mittente" />
                    <Label htmlFor="vettore-mittente" className="font-normal cursor-pointer">Mittente</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="destinatario" id="vettore-destinatario" />
                    <Label htmlFor="vettore-destinatario" className="font-normal cursor-pointer">Destinatario</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="manuale" id="vettore-manuale" />
                    <Label htmlFor="vettore-manuale" className="font-normal cursor-pointer">Manuale</Label>
                  </div>
                </RadioGroup>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-slate-700">Identificazione Vettore</Label>
                <Input
                  value={formData.identificazione_vettore}
                  onChange={(e) => handleInputChange("identificazione_vettore", e.target.value)}
                  placeholder={tipoVettore === "manuale" ? "Inserisci manualmente (es. Targa veicolo, nome trasportatore)" : "Compilato automaticamente"}
                  className="mt-1"
                  data-testid="vettore"
                  readOnly={tipoVettore !== "manuale"}
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Causale Trasporto (DDT)</Label>
                {causali.length > 0 ? (
                  <Select
                    value={formData.causale_text}
                    onValueChange={(value) => handleInputChange("causale_text", value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="select-causale">
                      <SelectValue placeholder="Seleziona causale" />
                    </SelectTrigger>
                    <SelectContent>
                      {causali.map((c) => (
                        <SelectItem key={c.id} value={c.testo}>
                          {c.testo}{c.predefinito ? " ★" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={formData.causale_text}
                    onChange={(e) => handleInputChange("causale_text", e.target.value.toUpperCase())}
                    placeholder="es. RESTITUZIONE C/ALLEVAMENTO"
                    className="mt-1"
                    data-testid="causale-input"
                  />
                )}
                {causali.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    Nessuna causale in archivio. Aggiungile da: Archivi → Causali
                  </p>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Data Documento</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal mt-1" data-testid="data-documento-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(dataDocumento, "dd/MM/yyyy", { locale: it })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dataDocumento} onSelect={(date) => date && setDataDocumento(date)} locale={it} />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button
              type="button"
              disabled={loading}
              onClick={(e) => handleSubmit(e, 'ddr')}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide h-12"
              data-testid="submit-ddr-btn"
            >
              {loadingType === 'ddr' ? "Creazione..." : (
                <><Save className="w-5 h-5 mr-2" />Crea DDR</>
              )}
            </Button>
            <Button
              type="button"
              disabled={loading}
              onClick={(e) => handleSubmit(e, 'ddt')}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold uppercase tracking-wide h-12"
              data-testid="submit-ddt-btn"
            >
              {loadingType === 'ddt' ? "Creazione..." : (
                <><Truck className="w-5 h-5 mr-2" />Crea DDT</>
              )}
            </Button>
            <Button
              type="button"
              disabled={loading}
              onClick={(e) => handleSubmit(e, 'both')}
              className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide h-12"
              data-testid="submit-both-btn"
            >
              {loadingType === 'both' ? "Creazione..." : (
                <><Package className="w-5 h-5 mr-2" />Crea Entrambi</>
              )}
            </Button>
          </div>

          {/* Stampa Vuoto - genera PDF con NumDDR, Date, Colli, Peso vuoti */}
          <div className="border-t border-slate-200 pt-4">
            <p className="text-sm text-slate-600 mb-2 font-medium">
              Stampa con N° DDR, Date, Colli e Peso <span className="text-orange-600">VUOTI</span> (da compilare a mano):
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                onClick={(e) => handleSubmit(e, 'ddr', true)}
                className="border-orange-300 text-orange-600 hover:bg-orange-50 font-bold uppercase tracking-wide h-12"
                data-testid="submit-ddr-vuoto-btn"
              >
                {loadingType === 'ddr-vuoto' ? "Creazione..." : (
                  <><Save className="w-5 h-5 mr-2" />Stampa DDR Vuoto</>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                onClick={(e) => handleSubmit(e, 'ddt', true)}
                className="border-blue-300 text-blue-600 hover:bg-blue-50 font-bold uppercase tracking-wide h-12"
                data-testid="submit-ddt-vuoto-btn"
              >
                {loadingType === 'ddt-vuoto' ? "Creazione..." : (
                  <><Truck className="w-5 h-5 mr-2" />Stampa DDT Vuoto</>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                onClick={(e) => handleSubmit(e, 'both', true)}
                className="border-green-300 text-green-700 hover:bg-green-50 font-bold uppercase tracking-wide h-12"
                data-testid="submit-both-vuoto-btn"
              >
                {loadingType === 'both-vuoto' ? "Creazione..." : (
                  <><Package className="w-5 h-5 mr-2" />Stampa Entrambi Vuoti</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NewDDR;
